/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from 'react';
import {
  SafeAreaView,
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  FlatList,
  Image,
  TextInput,
  StatusBar,
  ActivityIndicator,
  Alert,
} from 'react-native';
import { Ionicons, MaterialCommunityIcons } from '@expo/vector-icons';
import { useNavigation, useFocusEffect } from '@react-navigation/native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import axios from 'axios';

// ==================== ✅ API CONFIGURATION ====================
const API_BASE_URL = 'https://api.jobeng.ai';
const API_ENDPOINTS = {
  projects: `${API_BASE_URL}/api/projects`,
  companyProfile: `${API_BASE_URL}/api/company/profile`,
};

// ✅ Axios instance with API key and auth interceptor
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'X-API-Key': '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef',
  },
});

api.interceptors.request.use(
  async (config) => {
    try {
      const token = await AsyncStorage.getItem('auth_token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      const companyId = await AsyncStorage.getItem('company_id')
                      || await AsyncStorage.getItem('company_id');
      if (companyId) {
        config.params = { ...config.params, company_id: parseInt(companyId), company_id: parseInt(companyId) };
      }
    } catch (error) {
      console.error('Error in request interceptor:', error);
    }
    return config;
  },
  (error) => Promise.reject(error)
);

export default function ManageProjectsScreen() {
  const navigation = useNavigation();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFilter, setSelectedFilter] = useState('All');
  const [projects, setProjects] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [companyLogo, setCompanyLogo] = useState(null);

  // Project status filters (dynamically derived from fetched projects)
  const [statusFilters, setStatusFilters] = useState(['All']);

  // Load company logo when screen comes into focus
  useFocusEffect(
    React.useCallback(() => {
      loadCompanyLogo();
    }, [])
  );

  const loadCompanyLogo = async () => {
    try {
      const cachedProfile = await AsyncStorage.getItem('company_profile');
      if (cachedProfile) {
        const profile = JSON.parse(cachedProfile);
        setCompanyLogo(profile.logo || null);
      }

      const companyId = await AsyncStorage.getItem('company_id');
      if (companyId) {
        const response = await api.get(API_ENDPOINTS.companyProfile, {
          params: { company_id: companyId },
        });
        if (response.data?.success && response.data.data && response.data.data.length > 0) {
          const latestProfile = response.data.data[0];
          setCompanyLogo(latestProfile.logo || null);
          await AsyncStorage.setItem('company_profile', JSON.stringify(latestProfile));
        }
      }
    } catch (error) {
      console.warn('Error loading company logo:', error);
    }
  };

  // Fetch projects from API
  const fetchProjects = async () => {
    try {
      const response = await api.get(API_ENDPOINTS.projects);
      let projectsData = [];
      if (response.data?.success && response.data.data) {
        projectsData = response.data.data;
      } else if (Array.isArray(response.data)) {
        projectsData = response.data;
      }
      const transformed = projectsData.map(transformProject);
      setProjects(transformed);

      // Update status filters based on actual project statuses
      const statuses = transformed.map(p => p.status).filter(Boolean);
      const uniqueStatuses = [...new Set(statuses)];
      setStatusFilters(['All', ...uniqueStatuses]);
    } catch (error) {
      console.error('Error fetching projects:', error);
      Alert.alert('Error', 'Failed to load projects. Please try again.');
      setProjects([]);
    }
  };

  // Transform API project object to UI format
  const transformProject = (apiProject) => {
    return {
      id: apiProject.id || String(Math.random()),
      title: apiProject.name || 'Untitled Project',
      description: apiProject.description || '',
      client: apiProject.client_name || apiProject.client_company || 'Unknown Client',
      location: apiProject.location || 'Unknown Location',
      startDate: apiProject.start_date || new Date().toISOString(),
      endDate: apiProject.deadline || apiProject.end_date || new Date().toISOString(),
      budget: apiProject.budget ? String(apiProject.budget) : '0',
      currency: 'GBP',
      progress: apiProject.progress || 0,
      status: apiProject.status || 'Planning',
      priority: apiProject.priority || 'Medium',
      team: Array.isArray(apiProject.team_members) ? apiProject.team_members.map(member => ({
        id: member.id || String(Math.random()),
        name: member.name || 'Team Member',
        role: member.role || '',
        avatar: member.avatar || 'https://randomuser.me/api/portraits/lego/1.jpg',
      })) : [],
      milestones: Array.isArray(apiProject.milestones) ? apiProject.milestones : [],
      risks: Array.isArray(apiProject.risks) ? apiProject.risks : [],
      tags: Array.isArray(apiProject.tags) ? apiProject.tags : [],
      recentUpdates: Array.isArray(apiProject.recent_updates) ? apiProject.recent_updates : [],
      objectives: apiProject.objectives || '',
      scope: apiProject.scope || '',
      deliverables: Array.isArray(apiProject.deliverables) ? apiProject.deliverables : [],
      assumptions: Array.isArray(apiProject.assumptions) ? apiProject.assumptions : [],
      constraints: Array.isArray(apiProject.constraints) ? apiProject.constraints : [],
    };
  };

  // Load all data on mount
  useEffect(() => {
    loadAllData();
  }, []);

  const loadAllData = async () => {
    setLoading(true);
    await fetchProjects();
    setLoading(false);
  };

  const onRefresh = async () => {
    setRefreshing(true);
    await fetchProjects();
    setRefreshing(false);
  };

  // Handle search and filter (case-insensitive)
  useEffect(() => {
    let result = projects;
    if (selectedFilter !== 'All') {
      result = result.filter(project => project.status?.toLowerCase() === selectedFilter.toLowerCase());
    }
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        project =>
          String(project.title ?? '').toLowerCase().includes(query) ||
          String(project.client ?? '').toLowerCase().includes(query) ||
          String(project.location ?? '').toLowerCase().includes(query) ||
          String(project.description ?? '').toLowerCase().includes(query)
      );
    }
    setFilteredProjects(result);
  }, [searchQuery, selectedFilter, projects]);

  // Handle project selection - Navigate to ProjectDetailsScreen with project ID
  const handleProjectSelect = project => {
    navigation.navigate('ProjectDetails', { projectId: project.id });
  };

  // Handle back button navigation
  const handleGoBack = () => {
    if (navigation.canGoBack()) {
      navigation.goBack();
    } else {
      console.log("Can't go back from ManageProjectsScreen");
    }
  };

  // Get status color
  const getStatusColor = status => {
    switch (status?.toLowerCase()) {
      case 'active': return '#10B981';
      case 'planning': return '#8B5CF6';
      case 'on hold': return '#F59E0B';
      case 'completed': return '#0e4e96';
      default: return '#64748B';
    }
  };

  // Get priority color
  const getPriorityColor = priority => {
    switch (priority?.toLowerCase()) {
      case 'high': return '#EF4444';
      case 'medium': return '#F59E0B';
      case 'low': return '#10B981';
      default: return '#64748B';
    }
  };

  // Format date
  const formatDate = dateString => {
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'Invalid Date';
      return date.toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      });
    } catch {
      return 'Invalid Date';
    }
  };

  // Format currency
  const formatCurrency = (amount, currencyCode = 'GBP') => {
    const numericAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    if (isNaN(numericAmount)) return '';
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: currencyCode,
    }).format(numericAmount);
  };

  // Render progress bar
  const renderProgressBar = progress => (
    <View style={styles.progressBarContainer}>
      <View style={styles.progressBar}>
        <View
          style={[
            styles.progressFill,
            {
              width: `${progress}%`,
              backgroundColor:
                progress < 25 ? '#EF4444'
                  : progress < 50 ? '#F59E0B'
                  : progress < 75 ? '#10B981'
                  : '#0e4e96',
            },
          ]}
        />
      </View>
      <Text style={styles.progressText}>{progress}%</Text>
    </View>
  );

  // Render grid item
  const renderGridItem = ({ item }) => {
    const statusColor = getStatusColor(item.status);
    const priorityColor = getPriorityColor(item.priority);

    return (
      <TouchableOpacity
        style={styles.gridCard}
        onPress={() => handleProjectSelect(item)}
      >
        <View style={styles.gridCardHeader}>
          <View style={[styles.statusBadge, { backgroundColor: statusColor }]}>
            <Text style={styles.statusText}>{item.status}</Text>
          </View>
          <View style={[styles.priorityBadge, { backgroundColor: priorityColor }]}>
            <Text style={styles.priorityText}>{item.priority}</Text>
          </View>
        </View>

        <Text style={styles.gridCardTitle} numberOfLines={2}>
          {item.title}
        </Text>
        <Text style={styles.gridCardClient} numberOfLines={1}>
          {item.client}
        </Text>

        <View style={styles.gridCardDetail}>
          <Ionicons name="location-outline" size={14} color="#64748B" />
          <Text style={styles.gridCardDetailText} numberOfLines={1}>
            {item.location}
          </Text>
        </View>

        <View style={styles.gridCardDetail}>
          <Ionicons name="calendar-outline" size={14} color="#64748B" />
          <Text style={styles.gridCardDetailText}>
            {formatDate(item.startDate)} - {formatDate(item.endDate)}
          </Text>
        </View>

        <View style={styles.gridCardDetail}>
          <Ionicons name="cash-outline" size={14} color="#64748B" />
          <Text style={styles.gridCardDetailText}>
            {formatCurrency(item.budget, item.currency)}
          </Text>
        </View>

        <View style={styles.teamAvatarsContainer}>
          {item.team.slice(0, 3).map((member, index) => (
            <Image
              key={member.id}
              source={{ uri: (member.avatar && member.avatar.startsWith('http')) ? member.avatar : undefined }}
              style={[styles.teamAvatar, { marginLeft: index > 0 ? -10 : 0 }]}
            />
          ))}
          {item.team.length > 3 && (
            <View style={styles.teamAvatarMore}>
              <Text style={styles.teamAvatarMoreText}>+{item.team.length - 3}</Text>
            </View>
          )}
        </View>

        {renderProgressBar(item.progress)}

        <View style={styles.tagsContainer}>
          {item.tags.slice(0, 2).map((tag, index) => (
            <View key={index} style={styles.tag}>
              <Text style={styles.tagText}>{tag}</Text>
            </View>
          ))}
          {item.tags.length > 2 && (
            <View style={styles.moreTagsContainer}>
              <Text style={styles.moreTagsText}>+{item.tags.length - 2}</Text>
            </View>
          )}
        </View>
      </TouchableOpacity>
    );
  };

  // Render list item
  const renderListItem = ({ item }) => {
    const statusColor = getStatusColor(item.status);
    const priorityColor = getPriorityColor(item.priority);

    return (
      <TouchableOpacity
        style={styles.listCard}
        onPress={() => handleProjectSelect(item)}
      >
        <View style={styles.listCardHeader}>
          <Text style={styles.listCardTitle} numberOfLines={1}>
            {item.title}
          </Text>
          <View style={[styles.statusBadge, { backgroundColor: statusColor }]}>
            <Text style={styles.statusText}>{item.status}</Text>
          </View>
        </View>

        <View style={styles.listCardRow}>
          <View style={styles.listCardDetail}>
            <Ionicons name="business-outline" size={16} color="#64748B" />
            <Text style={styles.listCardDetailText} numberOfLines={1}>
              {item.client}
            </Text>
          </View>

          <View style={styles.listCardDetail}>
            <Ionicons name="location-outline" size={16} color="#64748B" />
            <Text style={styles.listCardDetailText} numberOfLines={1}>
              {item.location}
            </Text>
          </View>
        </View>

        <View style={styles.listCardRow}>
          <View style={styles.listCardDetail}>
            <Ionicons name="calendar-outline" size={16} color="#64748B" />
            <Text style={styles.listCardDetailText}>
              {formatDate(item.startDate)} - {formatDate(item.endDate)}
            </Text>
          </View>

          <View style={styles.listCardDetail}>
            <Ionicons name="cash-outline" size={16} color="#64748B" />
            <Text style={styles.listCardDetailText}>
              {formatCurrency(item.budget, item.currency)}
            </Text>
          </View>
        </View>

        <View style={styles.listCardRow}>
          <View style={styles.listCardDetail}>
            <MaterialCommunityIcons name="flag" size={16} color={priorityColor} />
            <Text style={[styles.listCardDetailText, { color: priorityColor }]}>
              {item.priority} Priority
            </Text>
          </View>

          <View style={styles.teamAvatarsContainer}>
            {item.team.slice(0, 3).map((member, index) => (
              <Image
                key={member.id}
                source={{ uri: (member.avatar && member.avatar.startsWith('http')) ? member.avatar : undefined }}
                style={[styles.teamAvatar, { marginLeft: index > 0 ? -10 : 0 }]}
              />
            ))}
            {item.team.length > 3 && (
              <View style={styles.teamAvatarMore}>
                <Text style={styles.teamAvatarMoreText}>+{item.team.length - 3}</Text>
              </View>
            )}
          </View>
        </View>

        {renderProgressBar(item.progress)}
      </TouchableOpacity>
    );
  };

  const renderEmptyList = () => (
    <View style={styles.emptyContainer}>
      <MaterialCommunityIcons name="clipboard-text-outline" size={60} color="#CBD5E1" />
      <Text style={styles.emptyTitle}>No Projects Found</Text>
      <Text style={styles.emptyText}>
        {searchQuery || selectedFilter !== 'All'
          ? 'No projects match your search criteria. Try different keywords or filters.'
          : 'There are no projects available at the moment. Add a new project to get started.'}
      </Text>
      <TouchableOpacity
        style={styles.addButtonEmptyState}
        onPress={() => navigation.navigate('AddProjectScreen')}
      >
        <Text style={styles.addButtonText}>Add New Project</Text>
      </TouchableOpacity>
    </View>
  );

  // Render header with company logo
  const renderHeader = () => (
    <View style={styles.header}>
      <TouchableOpacity style={styles.backButton} onPress={handleGoBack}>
        <Ionicons name="chevron-back" size={24} color="#fff" />
      </TouchableOpacity>

      <View style={styles.headerCenter}>
        {companyLogo ? (
          <Image source={{ uri: companyLogo || undefined }} style={styles.headerLogo} />
        ) : (
          <MaterialCommunityIcons name="office-building" size={30} color="#fff" />
        )}
        <Text style={styles.headerTitle}>Manage Projects</Text>
      </View>

      <TouchableOpacity
        onPress={() => navigation.navigate('NotificationsProjectScreen')}
        style={styles.headerButton}
      >
        <Ionicons name="notifications-outline" size={24} color="#fff" />
      </TouchableOpacity>
    </View>
  );

  return (
    <SafeAreaView style={styles.safeArea}>
      <StatusBar barStyle="light-content" backgroundColor="#0e4e96" />

      {renderHeader()}

      <View style={styles.searchContainer}>
        <View style={styles.searchInputContainer}>
          <Ionicons name="search" size={20} color="#64748B" style={styles.searchIcon} />
          <TextInput
            style={styles.searchInput}
            placeholder="Search projects..."
            placeholderTextColor="#94A3B8"
            value={searchQuery}
            onChangeText={setSearchQuery}
          />
          {searchQuery.length > 0 && (
            <TouchableOpacity onPress={() => setSearchQuery('')}>
              <Ionicons name="close-circle" size={20} color="#64748B" />
            </TouchableOpacity>
          )}
        </View>

        <View style={styles.viewToggleContainer}>
          <TouchableOpacity
            style={[styles.viewToggleButton, viewMode === 'grid' && styles.viewToggleButtonActive]}
            onPress={() => setViewMode('grid')}
          >
            <Ionicons name="grid" size={20} color={viewMode === 'grid' ? '#0e4e96' : '#64748B'} />
          </TouchableOpacity>

          <TouchableOpacity
            style={[styles.viewToggleButton, viewMode === 'list' && styles.viewToggleButtonActive]}
            onPress={() => setViewMode('list')}
          >
            <Ionicons name="list" size={20} color={viewMode === 'list' ? '#0e4e96' : '#64748B'} />
          </TouchableOpacity>
        </View>
      </View>

      <ScrollView
        horizontal
        showsHorizontalScrollIndicator={false}
        contentContainerStyle={styles.filtersContainer}
      >
        {statusFilters.map(filter => (
          <TouchableOpacity
            key={filter}
            style={[
              styles.filterButton,
              selectedFilter === filter && styles.filterButtonActive,
              selectedFilter === filter && { borderColor: getStatusColor(filter) },
            ]}
            onPress={() => setSelectedFilter(filter)}
          >
            <Text
              style={[
                styles.filterButtonText,
                selectedFilter === filter && styles.filterButtonTextActive,
                selectedFilter === filter && { color: getStatusColor(filter) },
              ]}
            >
              {filter === 'All' ? 'All' : filter.charAt(0).toUpperCase() + filter.slice(1)}
            </Text>
          </TouchableOpacity>
        ))}
      </ScrollView>

      <View style={styles.summaryContainer}>
        <View style={styles.summaryItem}>
          <Text style={styles.summaryValue}>{filteredProjects.length}</Text>
          <Text style={styles.summaryLabel}>Projects</Text>
        </View>
        <View style={styles.summaryItem}>
          <Text style={styles.summaryValue}>
            {filteredProjects.filter(p => p.status?.toLowerCase() === 'active').length}
          </Text>
          <Text style={styles.summaryLabel}>Active</Text>
        </View>
        <View style={styles.summaryItem}>
          <Text style={styles.summaryValue}>
            {filteredProjects.filter(p => p.priority?.toLowerCase() === 'high').length}
          </Text>
          <Text style={styles.summaryLabel}>High Priority</Text>
        </View>
        <View style={styles.summaryItem}>
          <Text style={styles.summaryValue}>
            {filteredProjects.filter(p => p.status?.toLowerCase() === 'completed').length}
          </Text>
          <Text style={styles.summaryLabel}>Completed</Text>
        </View>
      </View>

      {loading ? (
        <View style={styles.loadingContainerCentral}>
          <ActivityIndicator size={36} color="#0e4e96" />
          <Text style={styles.loadingText}>Loading projects...</Text>
        </View>
      ) : (
        <FlatList
          data={filteredProjects}
          keyExtractor={item => item.id.toString()}
          renderItem={viewMode === 'grid' ? renderGridItem : renderListItem}
          numColumns={viewMode === 'grid' ? 2 : 1}
          key={viewMode === 'grid' ? 'grid' : 'list'}
          contentContainerStyle={styles.projectsContainer}
          ListEmptyComponent={renderEmptyList}
          onRefresh={onRefresh}
          refreshing={refreshing}
        />
      )}

      <TouchableOpacity
        style={styles.floatingButton}
        onPress={() => navigation.navigate('AddProjectScreen')}
      >
        <Ionicons name="add" size={24} color="#fff" />
      </TouchableOpacity>
    </SafeAreaView>
  );
}

// Styles (unchanged)
const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: '#4A90E2',
  },
  header: {
    backgroundColor: '#4A90E2',
    paddingVertical: 15,
    paddingHorizontal: 20,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  backButton: {
    padding: 5,
  },
  headerCenter: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  headerLogo: {
    width: 30,
    height: 30,
    borderRadius: 15,
    resizeMode: 'cover',
  },
  headerTitle: {
    color: '#fff',
    fontSize: 20,
    fontWeight: 'bold',
    marginLeft: 10,
  },
  headerButton: {
    padding: 5,
  },
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 12,
    backgroundColor: '#fff',
    borderBottomWidth: 1,
    borderBottomColor: '#e2e8f0',
  },
  searchInputContainer: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f1f5f9',
    borderRadius: 8,
    paddingHorizontal: 12,
    height: 40,
  },
  searchIcon: {
    marginRight: 8,
  },
  searchInput: {
    flex: 1,
    fontSize: 16,
    color: '#0f172a',
  },
  viewToggleContainer: {
    flexDirection: 'row',
    marginLeft: 12,
    backgroundColor: '#f1f5f9',
    borderRadius: 8,
    padding: 4,
  },
  viewToggleButton: {
    padding: 6,
    borderRadius: 4,
  },
  viewToggleButtonActive: {
    backgroundColor: '#fff',
  },
  filtersContainer: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: '#fff',
    borderBottomWidth: 1,
    borderBottomColor: '#e2e8f0',
  },
  filterButton: {
    paddingVertical: 6,
    paddingHorizontal: 12,
    borderRadius: 16,
    marginRight: 8,
    backgroundColor: '#f1f5f9',
    borderWidth: 1,
    borderColor: '#e2e8f0',
  },
  filterButtonActive: {
    backgroundColor: '#fff',
    borderWidth: 1,
  },
  filterButtonText: {
    fontSize: 14,
    color: '#64748B',
  },
  filterButtonTextActive: {
    fontWeight: '600',
  },
  summaryContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 12,
    backgroundColor: '#fff',
    borderBottomWidth: 1,
    borderBottomColor: '#e2e8f0',
  },
  summaryItem: {
    alignItems: 'center',
  },
  summaryValue: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#0e4e96',
  },
  summaryLabel: {
    fontSize: 12,
    color: '#64748B',
    marginTop: 4,
  },
  loadingContainerCentral: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#f8fafc',
  },
  loadingText: {
    marginTop: 10,
    fontSize: 16,
    color: '#64748B',
  },
  projectsContainer: {
    padding: 12,
    backgroundColor: '#f8fafc',
  },
  gridCard: {
    flex: 1,
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 16,
    margin: 6,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
    shadowRadius: 10,
    elevation: 2,
  },
  gridCardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 12,
  },
  statusBadge: {
    paddingVertical: 4,
    paddingHorizontal: 8,
    borderRadius: 4,
  },
  statusText: {
    fontSize: 12,
    fontWeight: '500',
    color: '#fff',
  },
  priorityBadge: {
    paddingVertical: 4,
    paddingHorizontal: 8,
    borderRadius: 4,
  },
  priorityText: {
    fontSize: 12,
    fontWeight: '500',
    color: '#fff',
  },
  gridCardTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#0f172a',
    marginBottom: 6,
    height: 40,
  },
  gridCardClient: {
    fontSize: 14,
    color: '#64748B',
    marginBottom: 8,
  },
  gridCardDetail: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 6,
  },
  gridCardDetailText: {
    fontSize: 13,
    color: '#64748B',
    marginLeft: 6,
    flex: 1,
  },
  teamAvatarsContainer: {
    flexDirection: 'row',
    marginVertical: 8,
  },
  teamAvatar: {
    width: 28,
    height: 28,
    borderRadius: 14,
    borderWidth: 2,
    borderColor: '#fff',
  },
  teamAvatarMore: {
    width: 28,
    height: 28,
    borderRadius: 14,
    backgroundColor: '#e2e8f0',
    justifyContent: 'center',
    alignItems: 'center',
    marginLeft: -10,
    borderWidth: 2,
    borderColor: '#fff',
  },
  teamAvatarMoreText: {
    fontSize: 10,
    fontWeight: '600',
    color: '#64748B',
  },
  progressBarContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginVertical: 8,
  },
  progressBar: {
    flex: 1,
    height: 6,
    backgroundColor: '#e2e8f0',
    borderRadius: 3,
    marginRight: 8,
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    borderRadius: 3,
  },
  progressText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#64748B',
    width: 35,
    textAlign: 'right',
  },
  tagsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    marginTop: 4,
  },
  tag: {
    backgroundColor: '#f1f5f9',
    borderRadius: 4,
    paddingVertical: 4,
    paddingHorizontal: 8,
    marginRight: 6,
    marginBottom: 6,
  },
  tagText: {
    fontSize: 12,
    color: '#64748B',
  },
  moreTagsContainer: {
    backgroundColor: '#e2e8f0',
    borderRadius: 4,
    paddingVertical: 4,
    paddingHorizontal: 8,
    marginBottom: 6,
  },
  moreTagsText: {
    fontSize: 12,
    color: '#64748B',
    fontWeight: '500',
  },
  listCard: {
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 16,
    marginHorizontal: 6,
    marginVertical: 8,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.05,
    shadowRadius: 10,
    elevation: 2,
  },
  listCardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  listCardTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#0f172a',
    flex: 1,
    marginRight: 8,
  },
  listCardRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  listCardDetail: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  listCardDetailText: {
    fontSize: 14,
    color: '#64748B',
    marginLeft: 6,
    flex: 1,
  },
  emptyContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: 40,
    backgroundColor: '#f8fafc',
  },
  emptyTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#0f172a',
    marginTop: 16,
    marginBottom: 8,
  },
  emptyText: {
    fontSize: 14,
    color: '#64748B',
    textAlign: 'center',
    marginBottom: 24,
  },
  addButtonEmptyState: {
    backgroundColor: '#0e4e96',
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderRadius: 8,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  addButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
  },
  floatingButton: {
    position: 'absolute',
    bottom: 20,
    right: 20,
    width: 56,
    height: 56,
    borderRadius: 28,
    backgroundColor: '#0f172a',
    justifyContent: 'center',
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.2,
    shadowRadius: 5,
    elevation: 5,
  },
});